/**
 * LocationMapPicker - Interactive map for selecting/correcting client locations
 *
 * Features:
 * - Displays service region polygons with semi-transparent fill
 * - Draggable marker for client location
 * - Click anywhere on map to move marker
 * - Auto-centers on marker position
 * - Shows whether location is inside/outside service area
 */

import { useCallback, useRef, useEffect, useMemo } from 'react';
import { GoogleMap, useJsApiLoader, PolygonF, MarkerF } from '@react-google-maps/api';
import { GOOGLE_MAPS_LOADER_OPTIONS } from '../../lib/google-maps-config';
import type { ServiceRegion } from '../../lib/api';
import { pointInPolygon } from '../../hooks/useServiceRegions';

// =============================================================================
// TYPES
// =============================================================================

export interface LocationMapPickerProps {
  /** Current latitude (null if not set) */
  latitude: number | null;
  /** Current longitude (null if not set) */
  longitude: number | null;
  /** Callback when location changes (drag or click) */
  onLocationChange: (lat: number, lng: number) => void;
  /** Service regions to display as polygons */
  serviceRegions: ServiceRegion[];
  /** Height of the map container (default: 100%) */
  height?: string;
  /** Whether the map is read-only (no interaction) */
  readOnly?: boolean;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const MAP_CONTAINER_STYLE = {
  width: '100%',
  height: '100%',
  borderRadius: '12px',
};

/** Default center: San Francisco Bay Area */
const DEFAULT_CENTER = {
  lat: 37.7749,
  lng: -122.4194,
};

const DEFAULT_ZOOM = 10;
const MARKER_ZOOM = 14;

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Check if a point is inside ANY of the provided service regions.
 */
function isPointInAnyRegion(
  lat: number,
  lng: number,
  regions: ServiceRegion[]
): boolean {
  if (!regions || regions.length === 0) return true; // No regions = assume OK
  return regions.some((region) => pointInPolygon(lat, lng, region.polygon));
}

/**
 * Convert [lng, lat] polygon to Google Maps LatLngLiteral[]
 */
function polygonToLatLng(polygon: [number, number][]): google.maps.LatLngLiteral[] {
  return polygon.map(([lng, lat]) => ({ lat, lng }));
}

// =============================================================================
// COMPONENT
// =============================================================================

export function LocationMapPicker({
  latitude,
  longitude,
  onLocationChange,
  serviceRegions,
  height = '100%',
  readOnly = false,
}: LocationMapPickerProps) {
  const mapRef = useRef<google.maps.Map | null>(null);

  const { isLoaded, loadError } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

  // Determine center position
  const hasCoordinates = latitude !== null && longitude !== null;
  const center = useMemo(() => {
    if (hasCoordinates) {
      return { lat: latitude!, lng: longitude! };
    }
    return DEFAULT_CENTER;
  }, [hasCoordinates, latitude, longitude]);

  // Check if current location is inside service area
  const isInsideServiceArea = useMemo(() => {
    if (!hasCoordinates) return null;
    return isPointInAnyRegion(latitude!, longitude!, serviceRegions);
  }, [hasCoordinates, latitude, longitude, serviceRegions]);

  // Center map on marker when coordinates change
  useEffect(() => {
    if (mapRef.current && hasCoordinates) {
      mapRef.current.panTo({ lat: latitude!, lng: longitude! });
    }
  }, [hasCoordinates, latitude, longitude]);

  // Handle map load
  const handleMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  // Handle map click - move marker to clicked location
  const handleMapClick = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (readOnly || !e.latLng) return;
      onLocationChange(e.latLng.lat(), e.latLng.lng());
    },
    [readOnly, onLocationChange]
  );

  // Handle marker drag end
  const handleMarkerDragEnd = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (readOnly || !e.latLng) return;
      onLocationChange(e.latLng.lat(), e.latLng.lng());
    },
    [readOnly, onLocationChange]
  );

  // Render loading state
  if (!isLoaded) {
    return (
      <div
        className="flex items-center justify-center bg-sage-100 rounded-xl"
        style={{ height }}
      >
        <div className="text-sage-500">Loading map...</div>
      </div>
    );
  }

  // Render error state
  if (loadError) {
    return (
      <div
        className="flex items-center justify-center bg-red-50 rounded-xl"
        style={{ height }}
      >
        <div className="text-red-600 text-sm">Failed to load map</div>
      </div>
    );
  }

  return (
    <div className="relative" style={{ height }}>
      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={center}
        zoom={hasCoordinates ? MARKER_ZOOM : DEFAULT_ZOOM}
        onLoad={handleMapLoad}
        onClick={handleMapClick}
        options={{
          disableDefaultUI: true,
          zoomControl: true,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: false,
          gestureHandling: readOnly ? 'none' : 'cooperative',
        }}
      >
        {/* Service Region Polygons */}
        {serviceRegions.map((region) => (
          <PolygonF
            key={region.id}
            paths={polygonToLatLng(region.polygon)}
            options={{
              fillColor: region.color,
              fillOpacity: 0.15,
              strokeColor: region.color,
              strokeOpacity: 0.6,
              strokeWeight: 2,
              clickable: false,
            }}
          />
        ))}

        {/* Draggable Marker */}
        {hasCoordinates && (
          <MarkerF
            position={{ lat: latitude!, lng: longitude! }}
            draggable={!readOnly}
            onDragEnd={handleMarkerDragEnd}
            title="Drag to adjust location"
          />
        )}
      </GoogleMap>

      {/* Service Area Status Badge */}
      {hasCoordinates && isInsideServiceArea !== null && (
        <div
          className={`absolute bottom-3 left-3 px-3 py-1.5 rounded-lg text-sm font-medium shadow-md ${
            isInsideServiceArea
              ? 'bg-green-100 text-green-800'
              : 'bg-amber-100 text-amber-800'
          }`}
        >
          {isInsideServiceArea ? (
            <>✓ Inside service area</>
          ) : (
            <>⚠ Outside service area</>
          )}
        </div>
      )}

      {/* Click hint */}
      {!readOnly && (
        <div className="absolute top-3 left-3 px-2 py-1 bg-white/90 rounded text-xs text-sage-600 shadow">
          Click map or drag marker to set location
        </div>
      )}
    </div>
  );
}
